import useTracks from '@/compositions/useTracks';
import { nextTick, ref } from '@vue/composition-api';
import useStreams from '@/compositions/useStreams';
import useSocket from '@/compositions/useSocket';

const microphones = ref([]);

const volume = ref(null);

export default function useMicrophone(onLoadMicrophones) {
  const {
    stopTrack, replaceTrack, deactivateTrack,
  } = useTracks();
  const {
    requestMediaStream, microphoneDisabled, cameraDisabled, setOnEnd, lastMicrophoneDisabledState,
  } = useStreams();
  const { sendEvent } = useSocket();

  async function toggleMicrophone() {
    if (!microphoneDisabled.value) {
      await stopTrack('audio');
      if (cameraDisabled.value) {
        await stopTrack('video');
      }
      await deactivateTrack('audio');
      lastMicrophoneDisabledState.value = true;
    } else {
      const mediaStream = await requestMediaStream({
        audio: true,
        video: false,
      });
      const audioTrack = mediaStream?.getTracks().find((t) => t.kind === 'audio');
      if (!audioTrack) {
        return;
      }

      microphoneDisabled.value = false;
      lastMicrophoneDisabledState.value = false;

      setOnEnd(audioTrack);

      replaceTrack(audioTrack);
      sendEvent('action:audio', { state: !microphoneDisabled.value });
    }
  }

  async function getMicrophones() {
    const devices = await navigator.mediaDevices.enumerateDevices();
    return devices.filter((d) => {
      if (d.kind !== 'audioinput') {
        return false;
      }
      return true;
    });
  }

  async function loadMicrophones() {
    microphones.value = await getMicrophones();
  }

  navigator.mediaDevices.addEventListener('devicechange', async () => {
    const oldCamerasLength = microphones.value.length;
    await loadMicrophones();
    if (oldCamerasLength && typeof onLoadMicrophones === 'function') {
      onLoadMicrophones();
    }
  });

  async function getStreamByMicrophoneId(deviceId) {
    return requestMediaStream({
      audio: {
        deviceId: {
          exact: deviceId,
        },
      },
      video: true,
    });
  }

  async function changeMicrophone(deviceId) {
    if (!deviceId) {
      return;
    }
    await nextTick();
    if (!await stopTrack('audio')) {
      return;
    }
    const stream = await getStreamByMicrophoneId(deviceId);
    microphoneDisabled.value = false;
    const track = stream.getTracks().find((s) => s.kind === 'audio');
    replaceTrack(track);
  }

  return {
    toggleMicrophone,
    microphoneDisabled,
    microphones,
    changeMicrophone,
    loadMicrophones,
    volume,
    getStreamByMicrophoneId,
  };
}
